import React, { useState } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import CloseIcon from '@mui/icons-material/Close';
import { CirclePhoto } from '../../components';
import { CirclePhotoStack } from '../../components/CirclePhotoStack';
import { OfficialsSelectInput } from '../../components/OfficialsSelectInput';

export type OfficialMatchSelection = string | { $create: true };
type OfficialSelectChangeEvent = {
  target: {
    value?: {
      key?: string;
    };
  };
};

export interface CandidateOperationsPreviewCandidate {
  id: string;
  name: string;
  party?: string;
  photoPath?: string;
  hasVariants?: boolean;
  createAsNewOfficial?: boolean;
  officialMatch?: OfficialMatchSelection;
  onChangeOfficialMatch?: (officialMatch: OfficialMatchSelection) => Promise<void>;
}

interface CandidateOperationsPreviewProps {
  candidates: CandidateOperationsPreviewCandidate[];
  label?: string;
  detail?: string;
}

const CandidateOperationsPreview: React.FC<CandidateOperationsPreviewProps> = ({
  candidates,
  label,
  detail
}) => {
  const [modalOpen, setModalOpen] = useState(false);
  const [editingCandidateId, setEditingCandidateId] = useState<string | null>(null);
  const [savingCandidateId, setSavingCandidateId] = useState<string | null>(null);
  const [errorsByCandidateId, setErrorsByCandidateId] = useState<Record<string, string>>({});
  const photoPaths = candidates.map(candidate => candidate.photoPath);
  const photoHasVariants = candidates.map(candidate => candidate.hasVariants === true);
  const countLabel = label || `${candidates.length} candidate${candidates.length === 1 ? '' : 's'}`;

  const saveOfficialMatch = async (
    candidate: CandidateOperationsPreviewCandidate,
    officialMatch: OfficialMatchSelection
  ) => {
    if (!candidate.onChangeOfficialMatch || savingCandidateId) return;

    setSavingCandidateId(candidate.id);
    setErrorsByCandidateId(current => ({ ...current, [candidate.id]: '' }));
    try {
      await candidate.onChangeOfficialMatch(officialMatch);
      setEditingCandidateId(null);
    } catch (error) {
      setErrorsByCandidateId(current => ({
        ...current,
        [candidate.id]: error instanceof Error
          ? error.message
          : 'Unable to change the official match.'
      }));
    } finally {
      setSavingCandidateId(null);
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={() => {
          setModalOpen(true);
        }}
        disabled={candidates.length === 0}
        aria-label={candidates.length > 0 ? `View ${countLabel}` : countLabel}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          padding: 0,
          border: 0,
          background: 'transparent',
          color: 'inherit',
          font: 'inherit',
          textAlign: 'left',
          cursor: candidates.length > 0 ? 'pointer' : 'default'
        }}
      >
        {photoPaths.length === 1 && (
          <CirclePhoto photoPath={photoPaths[0]} hasVariants={photoHasVariants[0]} size="small" />
        )}
        {photoPaths.length > 1 && (
          <CirclePhotoStack
            photoPaths={photoPaths}
            photoHasVariants={photoHasVariants}
            size="small"
            containerWidth={110}
          />
        )}
        <div>
          <Typography variant="body2" style={{ color: '#172033', fontWeight: 600 }}>
            {countLabel}
          </Typography>
          {detail && (
            <Typography variant="caption" color="textSecondary">
              {detail}
            </Typography>
          )}
        </div>
      </button>

      <Dialog
        open={modalOpen}
        onClose={() => {
          if (!savingCandidateId) {
            setModalOpen(false);
            setEditingCandidateId(null);
          }
        }}
        fullWidth
        maxWidth="sm"
        aria-labelledby="candidate-operations-preview-title"
      >
        <DialogTitle id="candidate-operations-preview-title" style={{ paddingRight: '56px' }}>
          {countLabel}
          <IconButton
            onClick={() => {
              setModalOpen(false);
              setEditingCandidateId(null);
            }}
            aria-label="Close candidate list"
            size="small"
            disabled={Boolean(savingCandidateId)}
            style={{ position: 'absolute', top: '12px', right: '12px' }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {candidates.map((candidate, index) => {
            const isEditing = editingCandidateId === candidate.id;
            const isSaving = savingCandidateId === candidate.id;
            const error = errorsByCandidateId[candidate.id];

            return (
              <div
                key={`${candidate.id}-${index}`}
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '14px',
                  padding: '12px 0',
                  borderBottom: index < candidates.length - 1 ? '1px solid #e5e7eb' : undefined
                }}
              >
                <CirclePhoto photoPath={candidate.photoPath} hasVariants={candidate.hasVariants === true} size="small" />
                <div style={{ flexGrow: 1, minWidth: 0 }}>
                  <Typography variant="body1" style={{ color: '#172033', fontWeight: 600 }}>
                    {candidate.name}
                  </Typography>
                  {candidate.party && (
                    <Typography variant="body2" color="textSecondary">
                      {candidate.party}
                    </Typography>
                  )}
                  {isEditing && (
                    <div style={{ marginTop: '10px' }}>
                      <OfficialsSelectInput
                        value={null}
                        seedSearchText={candidate.name}
                        onChange={(event: OfficialSelectChangeEvent) => {
                          const officialKey = event.target.value?.key;
                          if (officialKey) saveOfficialMatch(candidate, officialKey);
                        }}
                        style={{ width: '100%' }}
                        className=""
                        office={null}
                        district={null}
                        placeholder="Search officials..."
                        disabled={Boolean(savingCandidateId)}
                        allowNewOption={false}
                      />
                      <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => saveOfficialMatch(candidate, { $create: true })}
                          disabled={Boolean(savingCandidateId)}
                        >
                          Create as new official
                        </Button>
                        <Button
                          size="small"
                          onClick={() => setEditingCandidateId(null)}
                          disabled={Boolean(savingCandidateId)}
                        >
                          Cancel
                        </Button>
                      </div>
                    </div>
                  )}
                  {error && (
                    <Typography variant="body2" color="error" role="alert" style={{ marginTop: '8px' }}>
                      {error}
                    </Typography>
                  )}
                </div>
                {isSaving && (
                  <CircularProgress size={20} aria-label={`Saving ${candidate.name}`} />
                )}
                {!isEditing && candidate.onChangeOfficialMatch && (
                  <Button
                    size="small"
                    onClick={() => {
                      setErrorsByCandidateId(current => ({ ...current, [candidate.id]: '' }));
                      setEditingCandidateId(candidate.id);
                    }}
                    disabled={Boolean(savingCandidateId)}
                    aria-label={`Edit official match for ${candidate.name}`}
                  >
                    Edit
                  </Button>
                )}
                {!isEditing && candidate.createAsNewOfficial && (
                  <Typography
                    variant="caption"
                    style={{
                      marginLeft: candidate.onChangeOfficialMatch ? 0 : 'auto',
                      padding: '4px 8px',
                      borderRadius: '12px',
                      color: '#1565c0',
                      backgroundColor: '#e3f2fd',
                      fontWeight: 600,
                      whiteSpace: 'nowrap'
                    }}
                  >
                    Create as new official
                  </Typography>
                )}
              </div>
            );
          })}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default CandidateOperationsPreview;
